import styled from 'styled-components'
import { css } from 'styled-components'

import {
  IconButton,
  StyledSkeleton,
  TypeStyle,
  TypeStyleHeadlineExtraExtraSmall,
  atXs,
  fromLg,
  fromMd,
  timingDefault,
  timingSlower,
  untilMd,
  zIndexForeground,
} from '@syconium/little-miss-figgy'
import { withHoverSupport } from '@syconium/little-miss-figgy'

import { IconBadge, IconSize } from '../../../../../../IconBadge'
import { iconBadgeStyles } from '../../../../../../IconBadge/styles'
import { tileAspectRatioPadding } from '../../../../styles'

const fromCustomSm = `@media screen and (min-width: 370px)` // necessary due to the tile layout being extremely tight

export const ShowMore = styled.div<{ $hierarchyVariant: boolean }>`
  padding: ${o => (o.$hierarchyVariant ? 0 : o.theme.spacing(1.5, 1))};
  min-width: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  box-sizing: border-box;
  background: ${o => (o.$hierarchyVariant ? 'none' : o.theme.color.fill.disabled.subtle)};
  border-radius: ${o => (o.$hierarchyVariant ? '0px' : '20px')};
  font-size: ${o => (o.$hierarchyVariant ? '12px' : '9px')};
  font-weight: 600;
  border-bottom: ${o => (o.$hierarchyVariant ? `1px solid black` : 'none')};
  margin-left: ${o => (o.$hierarchyVariant ? o.theme.spacing(0.5) : 0)};
  color: ${o =>
    o.$hierarchyVariant ? o.theme.color.stroke.neutral.text.tertiary.on.background : 'inherit'};

  span {
    display: none;
  }

  ${fromCustomSm} {
    padding: ${o => (o.$hierarchyVariant ? 0 : o.theme.spacing(1.5, 1.5))};

    span {
      display: inline;
    }
  }

  ${fromLg} {
    height: 20px;
    font-size: ${o => (o.$hierarchyVariant ? '12px' : '11px')};
    padding: ${o => (o.$hierarchyVariant ? 0 : o.theme.spacing(3, 2))};
  }
`

export const ShowMoreWrap = styled.div`
  display: flex;
  align-items: center;
`

export const ColorSelectorWrapper = styled.div<{
  $hierarchyVariant?: boolean
}>`
  margin-top: ${o => o.theme.spacing(2)};
  padding-top: ${o => (o.$hierarchyVariant ? o.theme.spacing(2) : 0)};
  display: grid;
  grid-template-columns: 112px 1fr;

  > div {
    display: flex;
    align-items: center;
  }
`

/**
 * Set z-index to create a new stacking context, so foreground stays behind filters.
 */
export const Body = styled.div`
  position: relative;
  z-index: 0;

  /* Hide tile action buttons on hover devices when not hovering/focusing the tile */
  ${withHoverSupport} {
    ${IconButton} {
      visibility: hidden;
    }

    &:hover,
    &:focus-within {
      ${IconButton} {
        visibility: visible;
      }
    }
  }

  ${StyledSkeleton}::after {
    padding-top: ${tileAspectRatioPadding};
  }
`

// We shrink the image slightly just to ensure it doesn't get considered as the LCP
// instead of the primary image. We could just not load it all together until hover,
// but that might be a worse user experience to have to wait on hover for a load.
// If you are feeling ambitious, you could wait to load till hover and then show a loading
// spinner until this image has fired a loaded event though.
export const SecondaryImageWrapper = styled.div`
  position: absolute;

  bottom: 8px;
  left: 8px;
  right: 8px;
  top: 8px;

  display: none;
  opacity: 0.05;
  transition: opacity ${timingSlower} ease;
  z-index: calc(${zIndexForeground} - 1);
  ${withHoverSupport} {
    display: block;
  }
`

const showSecondaryImageCSS = css`
  ${SecondaryImageWrapper} {
    z-index: ${zIndexForeground};
    display: block;
    opacity: 1;
    bottom: 0;
    left: 0;
    right: 0;
    top: 0;
  }
`

export const ImageContents = styled.div`
  margin-bottom: 8px;
  position: relative;

  &:has(:focus-visible) {
    ${showSecondaryImageCSS}
  }

  ${withHoverSupport} {
    &:hover {
      ${showSecondaryImageCSS}
    }
  }
`

export const PrimaryImageWrapper = styled.div`
  background: white;
  height: 100%;
  position: relative;
  width: 100%;
  z-index: ${zIndexForeground};
`

export const UnavailableOverlay = styled.div<{
  show: boolean
}>`
  align-items: center;
  background-color: ${o => o.theme.color.deprecated.stoneLighter};
  display: flex;
  height: 100%;
  font-size: 13px;
  font-weight: 500;
  justify-content: center;
  letter-spacing: 0.05em;
  opacity: ${o => (o.show ? 0.8 : 0)};
  pointer-events: none;
  position: absolute;
  text-transform: uppercase;
  top: 0;
  transition: opacity ${timingDefault} ease;
  width: 100%;
  z-index: ${zIndexForeground};

  ${fromMd} {
    font-size: 15px;
  }
`

export const DetailsContents = styled.div`
  ${untilMd} {
    padding-left: ${o => o.theme.spacing(2)};
  }
`

const lineHeight = 1.5
const maxNumberOfLines = 2

export const ProductName = styled(TypeStyle.HeadlineExtraExtraSmall)<{
  $hierarchyVariant?: boolean
}>`
  max-height: ${lineHeight * maxNumberOfLines}em;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: ${maxNumberOfLines};
  -webkit-box-orient: vertical;
  margin-top: ${o => (o.$hierarchyVariant ? o.theme.spacing(2) : 0)};

  /* Only allow underline-on-hover if the current device supports hover */
  &:hover {
    text-decoration: underline;
  }

  ${untilMd} {
    padding-left: ${o => (o.$hierarchyVariant ? 0 : o.theme.spacing(2))};
  }

  ${atXs} {
    font-size: 8px;
  }
`

export const ImageContainer = styled.div`
  position: relative;
  margin-bottom: 8px;
`

const ColorAndFit = styled(TypeStyle.BodyNano)`
  color: ${o => o.theme.color.deprecated.obsidianLighter};

  ${fromMd} {
    font-size: 13px;
  }

  span {
    font-weight: 600;
  }
`

export const ColorLine = styled(ColorAndFit)`
  line-height: 1.7;
  margin-top: 8px;
`

export const PriceDisplayWrapper = styled(TypeStyleHeadlineExtraExtraSmall)`
  margin-top: 8px;
`

export const FitLine = styled(ColorAndFit)`
  margin-top: ${o => o.theme.spacing(2)};
`

export const Color = styled.span`
  font-weight: bold;
  margin-inline-end: 4px;

  ${fromMd} {
    margin-inline-end: 12px;
  }
`

export const Badge = styled(TypeStyle.HeadlineMicro)<{
  colorOverride?: string | null
  $hierarchyVariant?: boolean
}>`
  color: ${o => o.colorOverride ?? '#496FE3'};
  font-size: 9px;

  ${o =>
    o.$hierarchyVariant
      ? css`
          padding: ${o => o.theme.spacing(1.5, 2)};
          background: ${o => o.theme.color.fill.subtle};
          border-radius: ${o => o.theme.borderRadius.sm};
          display: inline-block;
          font-size: 9px;
          font-weight: 700;
          line-height: 10px;
          margin: ${o.theme.spacing(2, 2, 0, 0)};

          ${fromMd} {
            font-size: 9px;
          }
        `
      : css`
          margin-bottom: ${o.theme.spacing(2)};
          padding-top: 0;

          ${untilMd} {
            padding-left: ${o.theme.spacing(2)};
          }
        `}

  ${fromMd} {
    font-size: ${o => (o.$hierarchyVariant ? '9px' : '11px')};
  }
`

export const PurchasedBadge = styled(Badge)<{
  $hierarchyVariant?: boolean
}>``

export const StyledIconBadge = styled(IconBadge)<{
  iconBadgePlacement: string
  size: IconSize
}>`
  ${o => (iconBadgeStyles[o.iconBadgePlacement] ?? iconBadgeStyles['top left'])?.[o.size]}
  position: absolute;
  z-index: ${zIndexForeground};
`

export const SiteWidePromoWrap = styled(TypeStyle.BodyNano)`
  color: ${o => o.theme.color.deprecated.red.error};
  font-weight: 600;
  padding-top: 6px;
  text-transform: uppercase;
`

export const SelectColorsOnSaleBadge = styled(TypeStyle.BodyNano)`
  color: ${o => o.theme.color.text.critical.on.background};
  font-weight: 600;
  padding-top: 6px;
  text-transform: uppercase;
`
